/**
 * Team Analytics Utilities
 * Simple analytics calculation for league teams
 */

/**
 * Calculate team analytics from game results
 * @param {Array} gameResults - Array of game result objects
 * @returns {Object} Analytics object
 */
export const calculateTeamAnalytics = (gameResults = []) => {
  if (gameResults.length === 0) {
    return {
      gamesPlayed: 0,
      wins: 0,
      losses: 0,
      totalScore: 0,
      averageScore: 0,
      bestScore: 0,
      winPercentage: 0,
      lastGameDate: null,
      currentStreak: 0,
      longestStreak: 0
    };
  }

  // Sort games by date (most recent first)
  const sortedGames = gameResults.sort((a, b) => {
    const dateA = a.gameDate?.toDate?.() || new Date(a.gameDate);
    const dateB = b.gameDate?.toDate?.() || new Date(b.gameDate);
    return dateB - dateA;
  });

  const gamesPlayed = sortedGames.length;
  const wins = sortedGames.filter(game => game.won === true).length;
  const losses = gamesPlayed - wins;
  const totalScore = sortedGames.reduce((sum, game) => sum + (game.score || 0), 0);
  const averageScore = gamesPlayed > 0 ? totalScore / gamesPlayed : 0;
  const bestScore = Math.max(...sortedGames.map(game => game.score || 0));
  const winPercentage = gamesPlayed > 0 ? (wins / gamesPlayed) * 100 : 0;
  const lastGameDate = sortedGames[0]?.gameDate;

  // Calculate current streak
  let currentStreak = 0;
  let longestStreak = 0;
  let tempStreak = 0;
  let lastResult = null;

  for (const game of sortedGames) {
    if (game.won === true) {
      if (lastResult === true) {
        tempStreak++;
      } else {
        tempStreak = 1;
      }
      if (currentStreak === 0) currentStreak = tempStreak;
    } else {
      if (lastResult === false) {
        tempStreak++;
      } else {
        tempStreak = 1;
      }
      if (currentStreak === 0) currentStreak = -tempStreak;
    }
    longestStreak = Math.max(longestStreak, Math.abs(tempStreak));
    lastResult = game.won;
  }

  return {
    gamesPlayed,
    wins,
    losses,
    totalScore,
    averageScore: Math.round(averageScore * 100) / 100,
    bestScore,
    winPercentage: Math.round(winPercentage * 100) / 100,
    lastGameDate,
    currentStreak,
    longestStreak
  };
};

/**
 * Calculate team rankings
 * @param {Array} allTeams - Array of all team objects with analytics
 * @param {Object} teamAnalytics - Current team's analytics
 * @param {Object} teamLocation - Team's location (city, state)
 * @returns {Object} Rankings object
 */
export const calculateTeamRankings = (allTeams = [], teamAnalytics, teamLocation) => {
  if (!teamAnalytics || allTeams.length === 0) {
    return {
      city: { position: 0, totalTeams: 0, percentile: 0 },
      state: { position: 0, totalTeams: 0, percentile: 0 },
      national: { position: 0, totalTeams: 0, percentile: 0 }
    };
  }

  // Filter teams by location
  const cityTeams = allTeams.filter(team => 
    team.location?.city === teamLocation?.city
  );
  const stateTeams = allTeams.filter(team => 
    team.location?.state === teamLocation?.state
  );

  // Sort teams by average score (primary), then win percentage (secondary)
  const sortTeams = (teams) => {
    return teams.sort((a, b) => {
      const scoreA = a.analytics?.averageScore || 0;
      const scoreB = b.analytics?.averageScore || 0;
      if (scoreA !== scoreB) return scoreB - scoreA;
      
      const winA = a.analytics?.winPercentage || 0;
      const winB = b.analytics?.winPercentage || 0;
      return winB - winA;
    });
  };

  const sortedCityTeams = sortTeams(cityTeams);
  const sortedStateTeams = sortTeams(stateTeams);
  const sortedNationalTeams = sortTeams(allTeams);

  // Find current team's position
  const findPosition = (sortedTeams, teamId) => {
    const index = sortedTeams.findIndex(team => team.id === teamId);
    return index >= 0 ? index + 1 : 0;
  };

  const cityPosition = findPosition(sortedCityTeams, teamAnalytics.teamId);
  const statePosition = findPosition(sortedStateTeams, teamAnalytics.teamId);
  const nationalPosition = findPosition(sortedNationalTeams, teamAnalytics.teamId);

  // Calculate percentiles
  const calculatePercentile = (position, total) => {
    if (total === 0) return 0;
    return Math.round(((total - position + 1) / total) * 100);
  };

  return {
    city: {
      position: cityPosition,
      totalTeams: cityTeams.length,
      percentile: calculatePercentile(cityPosition, cityTeams.length)
    },
    state: {
      position: statePosition,
      totalTeams: stateTeams.length,
      percentile: calculatePercentile(statePosition, stateTeams.length)
    },
    national: {
      position: nationalPosition,
      totalTeams: allTeams.length,
      percentile: calculatePercentile(nationalPosition, allTeams.length)
    }
  };
};

/**
 * Format analytics for display
 * @param {Object} analytics - Analytics object
 * @returns {Object} Formatted analytics
 */
export const formatTeamAnalytics = (analytics) => {
  return {
    ...analytics,
    averageScore: analytics.averageScore?.toFixed(1) || '0.0',
    winPercentage: analytics.winPercentage?.toFixed(1) || '0.0',
    lastGameDate: analytics.lastGameDate ? 
      new Date(analytics.lastGameDate).toLocaleDateString() : 'Never'
  };
};

/**
 * Get team performance summary
 * @param {Object} analytics - Analytics object
 * @returns {String} Performance summary
 */
export const getTeamPerformanceSummary = (analytics) => {
  if (analytics.gamesPlayed === 0) {
    return 'No games played yet';
  }

  const { winPercentage, averageScore, currentStreak } = analytics;
  
  let summary = `Played ${analytics.gamesPlayed} games with ${winPercentage}% win rate`;
  
  if (averageScore > 0) {
    summary += ` and ${averageScore} average score`;
  }
  
  if (currentStreak > 0) {
    summary += `. Currently on a ${currentStreak} game winning streak!`;
  } else if (currentStreak < 0) {
    summary += `. Currently on a ${Math.abs(currentStreak)} game losing streak.`;
  }
  
  return summary;
};


